import { useEffect } from 'react'
import { Link, Navigate, useNavigate, useParams } from 'react-router-dom'

import {
  useAcceptInvitation,
  useInvitationPreview,
  useInvitationStatus,
  useMe,
} from '../../hooks/queries'
import { useTexte } from '../../texte'

/**
 * Die Einladungsseite.
 *
 * Sie ist auch ohne Anmeldung erreichbar -- ein Einladungslink landet
 * regelmäßig bei jemandem, der noch kein Konto hat. Angezeigt wird nur, um
 * welche Reise es geht und von wem; aus dem Verlauf steht hier nichts.
 *
 * Seit dem bestätigten Beitritt hat sie drei Zustände: einladen, warten,
 * drin. Welcher gilt, weiß nur der Server -- die Seite fragt ihn.
 */
export function JoinPage() {
  const texte = useTexte()
  const t = texte.anmeldung.einladung
  const { code = '' } = useParams()
  const me = useMe()
  const preview = useInvitationPreview(code)
  const status = useInvitationStatus(code, me.data !== undefined)
  const accept = useAcceptInvitation()
  const navigate = useNavigate()

  /*
   * **Zwei Wege nach drinnen, und sie gehören auseinandergehalten.**
   *
   * Wer eben erst zugestimmt hat, will in die Reise -- der wird
   * weitergeleitet. Wer den eigenen Link ein zweites Mal öffnet, liest längst
   * mit; hier wurde er bisher ebenso wortlos weitergeleitet.
   *
   * Aus dem Betrieb, und die Meldung war zunächst eine andere: „Beitritt hat
   * funktioniert ohne Freigabe durch den Reisenden (obwohl markiert)". Es war
   * keiner -- das Konto war schon Mitglied. **Von außen ist das nicht zu
   * unterscheiden**, und genau darin lag der Mangel: eine stille Weiterleitung
   * sieht aus wie ein Beitritt, der eben stattgefunden hat.
   */
  const geradeBeigetreten = accept.data?.trip?.id ?? null
  const schonDabei =
    accept.data === undefined && status.data?.state === 'joined' ? status.data.trip_id : null

  useEffect(() => {
    if (geradeBeigetreten !== null) navigate(`/t/${geradeBeigetreten}`, { replace: true })
  }, [geradeBeigetreten, navigate])

  /*
   * **Vor der Vorschau, und das ist der Punkt.**
   *
   * Ist der Link aufgebraucht oder abgelaufen, scheitert die Vorschau -- und
   * darunter stünde „Einladung ungültig". Für jemanden, der hier längst
   * mitliest, ist das eine Auskunft über etwas, das ihn nicht betrifft. Den
   * Titel liefert deshalb der Zustand mit.
   */
  if (schonDabei !== null) {
    return (
      <div className="centered">
        <div className="card">
          <h1>{status.data?.trip_title ?? preview.data?.trip_title ?? t.dieseReise}</h1>
          <p className="card__lead">{t.schonDabei}</p>
          <Link className="button" to={`/t/${schonDabei}`}>
            {t.zurReise}
          </Link>
        </div>
      </div>
    )
  }

  if (preview.isPending) {
    return (
      <div className="centered">
        <p className="empty__title">{t.wirdGeprueft}</p>
      </div>
    )
  }

  if (preview.isError) {
    return (
      <div className="centered">
        <div className="card">
          <h1>{t.ungueltig}</h1>
          <p className="card__lead">{preview.error.message}</p>
          <Link className="button" to="/">
            {t.weiterZurAnwendung}
          </Link>
        </div>
      </div>
    )
  }

  // Angeklopft und noch keine Antwort. Was hier steht, steht auch dann da,
  // wenn der Reisende abgelehnt hat -- eine mitgeteilte Ablehnung wäre eine
  // Aufforderung nachzufragen.
  const wartet = accept.data?.state === 'pending' || status.data?.state === 'pending'

  // Wer angeklopft hat und sonst nirgends mitliest, gehört auf die Wartetafel
  // und nicht auf eine zweite Karte mit demselben Text. Dort steht dasselbe --
  // und dazu ein Weg hinaus.
  if (wartet && me.data !== undefined && !me.data.may_use_app) {
    return <Navigate to="/" replace />
  }

  if (wartet) {
    return (
      <div className="centered">
        <div className="card">
          <h1>{preview.data.trip_title}</h1>
          <p className="card__lead">{t.angefragt(preview.data.owner_name)}</p>
          <Link className="button button--outline" to="/">
            {t.weiterZurAnwendung}
          </Link>
        </div>
      </div>
    )
  }

  return (
    <div className="centered">
      <div className="card">
        <h1>{preview.data.trip_title}</h1>
        <p className="card__lead">{t.laedtEin(preview.data.owner_name)}</p>

        <p className="notice notice--info">{t.nurDerAbsender(preview.data.owner_name)}</p>

        {preview.data.approval_required && (
          <p className="card__lead">{t.bestaetigtJeden(preview.data.owner_name)}</p>
        )}

        {accept.isError && <p className="notice" role="alert">{accept.error.message}</p>}

        {me.data ? (
          <div className="stack">
            <button
              className="button"
              type="button"
              disabled={accept.isPending}
              onClick={() => accept.mutate(code)}
            >
              {accept.isPending
                ? texte.allgemein.laeuft
                : preview.data.approval_required
                  ? t.beitrittAnfragen
                  : t.beitreten}
            </button>
            <p className="leise leise--eng">
              {t.angemeldetAls(me.data.display_name)}
            </p>
          </div>
        ) : (
          <div className="stack">
            <Link className="button" to={`/register?code=${preview.data.code}`}>
              {t.kontoUndBeitreten}
            </Link>
            <Link
              className="button button--outline"
              to="/login"
              state={{ from: `/join/${preview.data.code}` }}
            >
              {t.habeKonto}
            </Link>
          </div>
        )}
      </div>
    </div>
  )
}
